import { Dispatch, SetStateAction, useMemo } from 'react';
import { Table } from '../../../../table/Table';
import { TableRow } from '../../../../table/interfaces/table';
import { JobObject } from '../../../interfaces/job';
import { ListComponentBaseProps } from '../../../../entity-list/interface';
import { JobObjectStages } from './JobObjectStages';
import { JobObjectListGlobalContext } from './interface';

import { getObjectTableColumns } from './JobObjectsTableColumns';
import { useAnalyticsTracker } from '../../../../tracking/hooks/useAnalyticsTracker';
import { JobsTrackers } from '../../../jobs-trackers';
import { shouldRenderObjectDetails } from '../../../utils';

export function JobObjectsTable({
  data: objects,
  jobId,
  rowsExpandedState,
  setRowsExpandedState,
  tableProps,
  isLoadOnly,
  latencyNotApplicable
}: ListComponentBaseProps<JobObject[]> & {
  jobId: string;
  rowsExpandedState: JobObjectListGlobalContext['rowsExpandedState'];
  setRowsExpandedState: Dispatch<SetStateAction<JobObjectListGlobalContext['rowsExpandedState']>>;
  isLoadOnly: boolean;
  latencyNotApplicable: boolean;
}) {
  const { eventTrack } = useAnalyticsTracker();

  const toggleRowExpandedState = (objectName: string) => {
    eventTrack({
      action: JobsTrackers.JOB_TRACKER_JOB_OBJECTS_LIST_JOBS_TABLE_TOGGLE,
      properties: {
        expanded: !rowsExpandedState[objectName]
      }
    });

    setRowsExpandedState(s => ({
      ...s,
      [objectName]: !rowsExpandedState[objectName]
    }));
  };

  const rows: TableRow<JobObject>[] = useMemo(() => {
    return objects.map(object => ({
      data: object,
      tabIndex: 0,
      id: object.objectId,
      onClick: shouldRenderObjectDetails(isLoadOnly, object.objectStages)
        ? () => {
            toggleRowExpandedState(object.objectName);
          }
        : undefined
    }));
  }, [objects, rowsExpandedState, isLoadOnly]);

  return (
    <Table
      columns={
        isLoadOnly
          ? getObjectTableColumns(isLoadOnly, latencyNotApplicable).filter(
              col => col.id !== 'ingested'
            )
          : getObjectTableColumns(false, latencyNotApplicable)
      }
      rows={rows}
      forceFullWidth
      {...tableProps}
      globalContext={
        {
          jobId,
          rowsExpandedState,
          toggleRowExpandedState,
          isLoadOnly
        } as JobObjectListGlobalContext
      }
      RowSpanningBox={JobObjectStages}
    />
  );
}
